import React, { useEffect, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { IntakeAudience, IntakeJourney, IntakeJourneySection } from '../../types/aai';
import { Action } from '../../components/aa';

/*
 * The partner's Journey for one client: the client intake, section by
 * section. People and Tools are asked during setup, the partner's own
 * sections after it. Sessions sent to one person are not part of it.
 * Nothing unlocks in order: every member answers what is theirs whenever
 * they like, so each section shows how many of its people have finished.
 */

const AUDIENCE_LABEL: Record<IntakeAudience, string> = {
  everyone: 'Everyone',
  owners: 'Owner and AI Champion',
  heads: 'Department heads',
  employees: 'Employees',
};

const audienceLabel = (audience: IntakeAudience | undefined): string => (audience || 'everyone')
  .split(',').map((g) => AUDIENCE_LABEL[g] || g).join(', ');

type Status = { label: string; tone: string };

const DONE_TONE = 'bg-[#e6f2e1] text-[#2f6b22]';
const PROGRESS_TONE = 'bg-[#fff7b3] text-[#5c5200]';
const IDLE_TONE = 'bg-canvas-inset text-ink-muted';

const questionCount = (n: number): string => {
  if (n === 1) return '1 question';
  return `${n} questions`;
};

const peopleWord = (n: number): string => {
  if (n === 1) return '1 person';
  return `${n} people`;
};

const isFinished = (s: IntakeJourneySection): boolean => s.receivers > 0 && s.done === s.receivers;

const sectionStatus = (s: IntakeJourneySection): Status => {
  if (!s.receivers) return { label: 'Nobody yet', tone: IDLE_TONE };
  if (s.done === s.receivers) return { label: 'Done', tone: DONE_TONE };
  if (s.done > 0) return { label: 'In progress', tone: PROGRESS_TONE };
  return { label: 'Not started', tone: IDLE_TONE };
};

const percent = (done: number, total: number): number => {
  if (!total) return 0;
  return Math.round((done / total) * 100);
};

const Pill: React.FC<{ status: Status }> = ({ status }) => (
  <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.tone}`}>{status.label}</span>
);

const Group: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <section className="mt-7">
    <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h3 className="m-0 text-[15px] font-bold text-ink">{title}</h3>
      <small className="text-[12px] text-ink-muted">{sub}</small>
    </div>
    {children}
  </section>
);

const SectionCard: React.FC<{ section: IntakeJourneySection; number: number }> = ({ section, number }) => {
  const status = sectionStatus(section);
  const finished = isFinished(section);
  let badge: React.ReactNode = number;
  let badgeTone = 'bg-canvas-inset text-ink';
  if (finished) {
    badge = <Check size={15} aria-hidden />;
    badgeTone = 'bg-ink text-[#ffea00]';
  }
  const meta = [audienceLabel(section.audience), questionCount(section.questions.length)].join(' · ');
  return (
    <div className="grid grid-cols-[34px_1fr] gap-x-4 gap-y-3 rounded-[14px] border border-line bg-white px-5 py-4 sm:grid-cols-[34px_1fr_220px] sm:items-center">
      <span className={`grid h-[30px] w-[30px] place-items-center rounded-full font-mono text-[12px] font-bold ${badgeTone}`}>{badge}</span>
      <div className="min-w-0">
        <strong className="block text-[15px] font-bold text-ink">{section.title}</strong>
        {section.description && <p className="m-0 mt-0.5 text-[12.5px] leading-[1.5] text-ink-muted">{section.description}</p>}
        <span className="mt-1.5 block font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-soft">{meta}</span>
      </div>
      <div className="col-start-2 sm:col-start-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-[12px] text-ink-muted"><b className="font-semibold text-ink">{section.done}</b> of {peopleWord(section.receivers)}</span>
          <Pill status={status} />
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas-inset">
          <div className="h-full rounded-full bg-[#ffea00]" style={{ width: `${percent(section.done, section.receivers)}%` }} />
        </div>
      </div>
    </div>
  );
};

export const IntakeJourneyPanel: React.FC<{
  clientId: string;
  onEditIntake?: () => void;
  onPeople: () => void;
}> = ({ clientId, onEditIntake, onPeople }) => {
  const [data, setData] = useState<IntakeJourney | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    aaiApi.intakeJourney(clientId)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load the intake for this client.'); });
    return () => { live = false; };
  }, [clientId]);

  if (error) return <p className="journey-lede">{error}</p>;
  if (!data) return <p className="journey-lede">Loading the intake…</p>;

  const setup = data.sections.filter((s) => s.fixed);
  const own = data.sections.filter((s) => !s.fixed);
  const finished = data.sections.filter(isFinished).length;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="journey-lede" style={{ margin: 0 }}>
          What everyone at this client answers. <b>The opening questions come first, then the business, department, person and tool intakes, then your own sections.</b>
        </p>
        <div className="flex flex-wrap gap-2">
          <Action size="sm" variant="outline" onClick={onPeople}>See people</Action>
          {onEditIntake && <Action size="sm" variant="primary" onClick={onEditIntake}>Edit the intake</Action>}
        </div>
      </div>

      <div className="ov-stat mt-5 md:!grid-cols-2">
        <div className="s"><b>{data.people}</b><small>people at this client</small></div>
        <div className="s"><b>{finished} / {data.sections.length}</b><small>sections finished by everyone</small></div>
      </div>

      {setup.length > 0 && (
        <Group title="From Agents Anywhere" sub="The five intakes, with your changes">
          <div className="flex flex-col gap-2.5">
            {setup.map((s, i) => <SectionCard key={s.id} section={s} number={i + 1} />)}
          </div>
        </Group>
      )}

      <Group title="Your sections" sub="In your order, to who you chose">
        {own.length > 0 && (
          <div className="flex flex-col gap-2.5">
            {own.map((s, i) => <SectionCard key={s.id} section={s} number={setup.length + i + 1} />)}
          </div>
        )}
        {own.length === 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-[14px] border border-dashed border-line-strong bg-white px-5 py-4">
            <div>
              <strong className="block text-[14px] font-semibold text-ink">No sections of your own yet</strong>
              <small className="mt-0.5 block text-[12.5px] text-ink-muted">Add the questions you want every person here to answer after setup.</small>
            </div>
            {onEditIntake && <Action size="sm" variant="outline" icon={<Plus size={14} />} onClick={onEditIntake}>Add a section</Action>}
          </div>
        )}
      </Group>

    </>
  );
};

export default IntakeJourneyPanel;
